import { type CSSProperties, Component, type ErrorInfo, type PropsWithChildren, type ReactNode } from 'react';

import Colors from 'src/common/Colors';

const errorContainerStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',

  position: 'absolute',
  top: 0,
  bottom: 0,
  left: 0,
  right: 0,
};

const errorTextStyle: CSSProperties = {
  color: Colors.dangerHeavy,
  fontSize: '2em',
  marginBottom: '0.25em',
  filter: 'drop-shadow(0px 3px 5px rgba(0,0,0,0.2))',
};

const errorDetailsStyle: CSSProperties = {
  fontSize: '1em',
  margin: '0 2em 2em 2em',
  textAlign: 'center',
  maxHeight: '10em',
  overflowY: 'scroll',
};

const restartButtonStyle: CSSProperties = {
  cursor: 'pointer',
  fontSize: '1.5em',
  marginBottom: '1em',
  padding: '0.25em 0.5em',
};

export class ErrorBoundary extends Component<
  PropsWithChildren<{ onError?: (error: Error, errorInfo: ErrorInfo) => void }>,
  { error: Error | null }
> {
  public state: { error: Error | null } = { error: null };

  public componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    this.setState({ error });
    this.props.onError?.(error, errorInfo);

    console.error(error);
  }

  private reset() {
    console.warn('Resetting error state');

    this.setState({ error: null });

    window.location.hash = '';
  }

  public render(): ReactNode {
    if (this.state.error !== null) {
      return (
        <div style={errorContainerStyle}>
          <span style={errorTextStyle}>Unhandled Error</span>
          {this.state.error.message && (
            <span className="custom-scrollbar" style={errorDetailsStyle}>
              {this.state.error.message}
            </span>
          )}
          <button type="button" style={restartButtonStyle} onClick={() => this.reset()}>
            Restart
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}
